import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingBag, Package, Footprints, Users, PartyPopper, HandHelping,
  ArrowRight, MessageCircle, Check, MapPin, ShieldCheck, IdCard, Receipt,
  Headphones, Star, ChevronDown, Store, X, Home as HomeIcon, Sparkles,
} from "lucide-react";
import PricingSection from "@/components/PricingSection";
import PartnerQuickForm from "@/components/PartnerQuickForm";
import MediaSlot from "@/components/MediaSlot";
import { IconGrid } from "@/components/IconArt";
import WhyChela from "@/components/WhyChela";
import SeeHow from "@/components/SeeHow";
import ReviewVideo from "@/components/ReviewVideo";
import { useSettings } from "@/lib/settings";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBar from "@/components/MobileBar";
import { useLang } from "@/i18n";
import { track, waLink } from "@/lib/api";
import { SERVICES, FAQS, IMAGES } from "@/data/content";

const ICONS = { "shopping-bag": ShoppingBag, package: Package, footprints: Footprints, users: Users, "party-popper": PartyPopper, "hand-helping": HandHelping, home: HomeIcon };

function Section({ id, children, className = "" }) {
  return <section id={id} className={`chela-section ${className}`}>{children}</section>;
}

export default function Home() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useSettings();

  useEffect(() => {
    track("page_view", { page: "home" });
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 60);
    }
  }, [location]);

  const price = settings.base_hourly_price;
  const currency = settings.currency;
  const cities = settings.cities;
  const fillPrices = (s) => s.replace("{std}", `${currency}${price}`);

  const goBook = (source) => { track("book_click", { source }); navigate("/book"); };
  const onWhatsApp = (source) => {
    track("whatsapp_click", { source });
    window.open(waLink("Hi CHELA! I'd like to book an extra pair of hands."), "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-20 md:pb-0">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="chela-container pt-8 sm:pt-12 lg:pt-16 pb-16 sm:pb-20 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6 animate-rise">
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#E05624] bg-[#FFF1EC] border border-[#F6D9CD] rounded-full px-4 py-1.5">
              <span className="h-2 w-2 rounded-full bg-[#E05624] animate-pulse" /> {t("hero_badge")}
            </span>
            <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-[#121316] whitespace-pre-line">
              {t("hero_h1")}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#5A5D65] max-w-xl leading-relaxed">{t("hero_sub")}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="hero-book-btn" onClick={() => goBook("hero")} className="inline-flex items-center gap-2 h-14 px-8 rounded-full btn-saffron text-base font-bold">
                {t("book")} <ArrowRight size={18} />
              </button>
              <button data-testid="hero-join-btn" onClick={() => navigate("/join")} className="inline-flex items-center gap-2 h-14 px-7 rounded-full border-2 border-[#121316] text-base font-bold text-[#121316] hover:bg-[#121316] hover:text-[#FAF8F5] transition-colors">
                {t("foot_join")}
              </button>
            </div>
            <p data-testid="hero-form-note" className="mt-4 text-sm font-semibold text-[#3A3D45]">{t("hero_wa_cta")}</p>
            <button data-testid="hero-whatsapp-btn" onClick={() => onWhatsApp("hero")} className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#1B8A4B] hover:underline">
              <MessageCircle size={16} /> {t("whatsapp")}
            </button>

            <div data-testid="hero-diwali" className="mt-8 flex w-fit items-center gap-2 rounded-full bg-[#121316] text-[#FFD27A] px-4 py-2 text-sm sm:text-base font-extrabold tracking-wide uppercase">
              <Sparkles size={16} /> {t("diwali_price")}
            </div>
            <div data-testid="hero-big-price" className="mt-3 flex w-fit flex-wrap items-center gap-x-5 gap-y-2 rounded-3xl bg-[#E05624] text-white px-6 py-4 shadow-[0_18px_40px_rgba(224,86,36,0.35)] animate-rise">
              <div className="leading-none"><span className="font-display font-extrabold text-6xl sm:text-7xl tracking-tight">{currency}{price}</span><span className="ml-1 text-lg font-bold opacity-90">{t("per_hour")}</span></div>
              <div className="text-sm font-bold leading-snug max-w-[13rem]">
                <div className="flex items-center gap-1.5"><MapPin size={15} /> Bhopal</div>
                <div data-testid="hero-offer" className="mt-1 bg-white text-[#E05624] rounded-full px-3 py-1 text-xs font-extrabold inline-block">{t("offer_line")}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 animate-rise" style={{ animationDelay: ".12s" }}>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden border border-[#E5E0D8] shadow-[0_30px_70px_rgba(0,0,0,0.14)]">
                <MediaSlot slot={settings.media?.hero_video} fallback={<IconGrid kinds={["personal", "family", "errands", "homehelp"]} lang={lang} className="h-[360px] sm:h-[460px] lg:h-[560px]" />} mode="tap" alt="CHELA" className="w-full h-[360px] sm:h-[460px] lg:h-[560px] object-cover" testid="hero-video" />
              </div>
              <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl border border-[#E5E0D8] shadow-xl px-5 py-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#FFF1EC] flex items-center justify-center"><HandHelping size={20} className="text-[#E05624]" /></div>
                <div>
                  <div className="text-sm font-bold text-[#121316] font-display">Book by the hour</div>
                  <div className="text-xs text-[#5A5D65]">One reliable person. A few clicks.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="chela-container pb-12">
          <div data-testid="hero-example-strip" className="rounded-2xl bg-[#121316] text-[#FAF8F5] px-6 py-5 flex items-start gap-3">
            <HandHelping size={20} className="text-[#F0894F] shrink-0 mt-0.5" />
            <p className="font-semibold leading-relaxed">{t("hero_example")}</p>
          </div>
        </div>
      </section>

      <SeeHow />

      <ReviewVideo kind="customer" />

      <WhyChela />

      <TrustSection t={t} />

      {/* PROBLEM */}
      <Section className="bg-[#121316] text-[#FAF8F5] relative grain">
        <div className="chela-container relative">
          <span className="eyebrow">{t("problem_eyebrow")}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-tight">
            {t("problem_h2")}
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{t("problem_p")}</p>
          <p className="mt-8 text-xl sm:text-2xl font-display font-bold text-[#F0894F]">{t("problem_line")}</p>
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="bg-[#F3EFEA]">
        <div className="chela-container">
          <span className="eyebrow">{t("srv_eyebrow")}</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#121316] max-w-2xl">{t("srv_h2")}</h2>
            <p className="text-[#5A5D65] max-w-md">{t("srv_p")}</p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || HandHelping;
              return (
                <div key={s.id} data-testid={`service-card-${s.id}`} className="chela-card rounded-3xl p-7 flex flex-col">
                  <div className="h-12 w-12 rounded-2xl bg-[#FFF1EC] flex items-center justify-center"><Icon size={22} className="text-[#E05624]" /></div>
                  <h3 className="mt-5 font-display font-bold text-xl text-[#121316]">{s[lang].title}</h3>
                  <ul className="mt-4 space-y-2.5 flex-1">
                    {s[lang].items.map((it, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-sm text-[#3A3D45]">
                        <Check size={16} className="text-[#E05624] mt-0.5 shrink-0" /> {it}
                      </li>
                    ))}
                  </ul>
                  {s[lang].note && (
                    <p className="mt-4 text-xs text-[#8A6A2E] bg-[#FCF4E6] border border-[#F0E2C4] rounded-xl px-3 py-2">{s[lang].note}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <button data-testid="services-book-btn" onClick={() => goBook("services")} className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full btn-dark text-base font-bold">
              {t("srv_cta")} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </Section>

      <PricingSection />

      {/* FESTIVAL */}
      <Section className="relative">
        <div className="chela-container">
          <div className="relative rounded-[2rem] overflow-hidden">
            <img src={settings.media?.customer_events_video?.poster || "/media/ai-events.jpg"} alt="" loading="lazy" className="w-full h-[440px] sm:h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121316] via-[#121316]/80 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="p-7 sm:p-14 max-w-xl text-[#FAF8F5]">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F0894F]">{t("fest_eyebrow")}</span>
                <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("fest_h2")}</h2>
                <p className="mt-5 text-white/80 text-lg">{t("fest_p")}</p>
                <button data-testid="festival-book-btn" onClick={() => goBook("festival")} className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full btn-saffron text-base font-bold">
                  {t("book")} <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW */}
      <Section id="how" className="bg-[#121316] text-[#FAF8F5]">
        <div className="chela-container">
          <span className="eyebrow">{t("how_eyebrow")}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">{t("how_h2")}</h2>

          <div className="mt-8 grid gap-3 grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="rounded-2xl border border-white/12 bg-white/[0.03] p-4 sm:p-6">
                <div className="text-[#F0894F] font-display font-extrabold text-2xl">0{n}</div>
                <h3 className="mt-2 font-display font-bold text-base sm:text-lg">{t(`how_${n}_t`)}</h3>
                <p className="mt-1.5 text-white/65 text-xs sm:text-sm leading-relaxed">{t(`how_${n}_d`)}</p>
              </div>
            ))}
          </div>

          <button data-testid="how-book-btn" onClick={() => goBook("how")} className="mt-10 inline-flex items-center gap-2 h-14 px-8 rounded-full btn-saffron text-base font-bold">
            {t("book")} <ArrowRight size={18} />
          </button>
        </div>
      </Section>

      {/* B2B */}
      <Section id="partner-teaser" className="bg-[#FFF1EC]">
        <div className="chela-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow">{t("b2b_eyebrow")}</span>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#121316]">{t("b2b_h2")}</h2>
            <p className="mt-5 text-lg text-[#5A5D65] max-w-xl">{t("b2b_p")}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Saree stores", "Jewellery", "Boutiques", "Wedding stores", "Furniture", "Electronics", "Gift stores", "Event businesses"].map((b) => (
                <span key={b} className="text-sm font-semibold text-[#121316] bg-white border border-[#F6D9CD] rounded-full px-3.5 py-1.5">{b}</span>
              ))}
            </div>
            <p className="mt-6 text-sm text-[#5A5D65] max-w-xl">{t("b2b_benefit")}</p>
            <button data-testid="b2b-partner-btn" onClick={() => { track("partner_click", { source: "home" }); navigate("/partner"); }} className="mt-7 inline-flex items-center gap-2 h-14 px-8 rounded-full btn-saffron text-base font-bold">
              <Store size={18} /> {t("b2b_cta")}
            </button>
          </div>
          <div className="lg:col-span-5">
            <PartnerQuickForm />
          </div>
        </div>
      </Section>

      {/* CITIES */}
      <Section id="cities">
        <div className="chela-container">
          <span className="eyebrow">{t("city_eyebrow")}</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#121316]">{t("city_h2")}</h2>
            <p className="text-[#5A5D65] max-w-md">{t("city_p")}</p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cities.map((c) => (
              <div key={c.name} data-testid={`city-${c.name.toLowerCase()}`} className={`rounded-3xl p-6 border flex items-center justify-between ${c.status === "live" ? "bg-[#121316] text-[#FAF8F5] border-[#121316]" : "chela-card"}`}>
                <div className="flex items-center gap-3">
                  <MapPin size={20} className={c.status === "live" ? "text-[#F0894F]" : "text-[#E05624]"} />
                  <span className="font-display font-bold text-xl">{c.name}</span>
                </div>
                {c.status === "live" ? (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#E8F7EE] text-[#1B8A4B]">LIVE / PILOT</span>
                ) : (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FCF4E6] text-[#8A6A2E]">{t("soon")}</span>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8">
            <button data-testid="cities-waitlist-btn" onClick={() => navigate("/book?waitlist=1")} className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full btn-dark text-base font-bold">
              {t("join_waitlist")} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-[#F3EFEA]">
        <div className="chela-container max-w-4xl">
          <span className="eyebrow">{t("faq_eyebrow")}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#121316]">{t("faq_h2")}</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => <FaqItem key={i} idx={i} q={f[lang].q} a={fillPrices(f[lang].a)} />)}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="chela-section bg-[#E05624] text-white relative overflow-hidden">
        <div className="chela-container text-center relative">
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">{t("final_h2")}</h2>
          <p className="mt-3 font-display font-bold text-2xl sm:text-3xl text-white/90">{t("final_p")}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button data-testid="final-book-btn" onClick={() => goBook("final")} className="inline-flex items-center gap-2 h-14 px-9 rounded-full bg-white text-[#E05624] font-bold text-base hover:bg-[#FFF1EC] transition-colors">
              {t("book")} <ArrowRight size={18} />
            </button>
            <button data-testid="final-whatsapp-btn" onClick={() => onWhatsApp("final")} className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#121316] text-white font-bold text-base hover:bg-black transition-colors">
              <MessageCircle size={18} /> {t("whatsapp")}
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <MobileBar />
    </div>
  );
}

const TRUST_ITEMS = [
  { icon: IdCard, key: "trust_i1", live: true },
  { icon: Receipt, key: "trust_i2", live: true },
  { icon: Check, key: "trust_i3", live: true },
  { icon: Headphones, key: "trust_i4", live: true },
  { icon: ShieldCheck, key: "trust_i5", live: true },
  { icon: Star, key: "trust_i6", live: true },
];

function TrustSection({ t }) {
  return (
    <Section id="trust">
      <div className="chela-container">
        <span className="eyebrow">{t("trust_eyebrow")}</span>
        <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#121316]">{t("trust_h2")}</h2>
        <p className="mt-5 text-lg text-[#5A5D65] max-w-2xl">{t("trust_p")}</p>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TRUST_ITEMS.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`trust-card-${i}`} className="chela-card rounded-3xl p-6 flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#FFF1EC] flex items-center justify-center shrink-0"><Icon size={20} className="text-[#E05624]" /></div>
                <div>
                  <div className="font-display font-bold text-lg text-[#121316]">{t(f.key)}</div>
                  <span className={`mt-2 inline-block text-xs font-bold px-2.5 py-1 rounded-full ${f.live ? "bg-[#E8F7EE] text-[#1B8A4B]" : "bg-[#FCF4E6] text-[#8A6A2E]"}`}>
                    {f.live ? t("live_label") : t("soon")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div data-testid="trust-never-box" className="mt-8 rounded-3xl border-2 border-[#121316] bg-white p-6 sm:p-7">
          <div className="font-display font-bold text-xl text-[#121316]">{t("never_title")}</div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {[1, 3, 4].map((n) => (
              <li key={n} className="flex items-start gap-2.5 text-[#3A3D45]"><X size={18} className="text-[#E05624] mt-0.5 shrink-0" /> {t(`never_${n}`)}</li>
            ))}
          </ul>
          <p data-testid="trust-allow-line" className="mt-4 flex items-start gap-2.5 font-semibold text-[#121316]"><Check size={18} className="text-[#1B8A4B] mt-0.5 shrink-0" /> {t("never_allow")}</p>
        </div>
        <p data-testid="trust-note" className="mt-6 text-sm font-semibold text-[#3A3D45]">{t("trust_note")}</p>
      </div>
    </Section>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="chela-card rounded-2xl overflow-hidden" style={{ transform: "none" }}>
      <button data-testid={`faq-q-${idx}`} onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 text-left p-5 sm:p-6">
        <span className="font-display font-bold text-lg text-[#121316]">{q}</span>
        <ChevronDown size={20} className={`text-[#E05624] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-6 -mt-1 text-[#5A5D65] leading-relaxed">{a}</div>}
    </div>
  );
}
